import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { HtmlTextFormatter, NewLineFormatter } from "../../../components/Utils";
import {
  fetchOutboundCommunication,
  fetchOutboundCommunicationPdf,
} from "../../../api/outbound_communications";
import { TinyDocumentSection } from "../../../components/dashboard/documents/tinyDocumentSection";

const formatWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("nb-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const OutboundCommunicationShow = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchOutboundCommunication(id)
      .then((data) => {
        if (!cancelled) setRow(data);
      })
      .catch(() => {
        if (!cancelled) toast.error(t("outbound_communications.load_error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  const handleExportPdf = async () => {
    if (!id || exporting) return;
    setExporting(true);
    try {
      const res = await fetchOutboundCommunicationPdf(id);
      if (res?.pdf_url) {
        window.open(res.pdf_url, "_blank", "noopener");
      } else {
        toast.error(t("outbound_communications.pdf_failed"));
      }
    } catch {
      toast.error(t("outbound_communications.pdf_failed"));
    } finally {
      setExporting(false);
    }
  };

  const headerActions = [
    {
      component: null,
      label: exporting
        ? t("outbound_communications.pdf_loading")
        : t("outbound_communications.export_pdf"),
      description: "",
      path: null,
      action: handleExportPdf,
      icon: DocumentArrowDownIcon,
      textColor: "text-blue-700",
      bgColor: "bg-white",
      hidden: !row?.id,
      menu: false,
    },
  ];

  return (
    <DashboardHeader
      title={row?.subject || t("outbound_communications.title")}
      variant="v2"
      actions={headerActions}
    >
      <div className="mx-auto max-w-4xl px-4 py-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            className="text-sm font-semibold text-[#1463C7]"
            onClick={() => navigate("/dashboard/communications")}
          >
            ← {t("outbound_communications.back")}
          </button>
          {row?.id ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-slate-50 disabled:opacity-60"
              onClick={handleExportPdf}
              disabled={exporting}
            >
              <DocumentArrowDownIcon className="h-5 w-5" aria-hidden />
              {exporting
                ? t("outbound_communications.pdf_loading")
                : t("outbound_communications.export_pdf")}
            </button>
          ) : null}
        </div>

        {loading ? (
          <ResourceLoading />
        ) : !row ? (
          <p className="text-slate-500">{t("outbound_communications.not_found")}</p>
        ) : (
          <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)]">
            <dl className="grid grid-cols-1 gap-0 sm:grid-cols-2">
              {[
                [t("outbound_communications.col_sent"), formatWhen(row.sent_at)],
                [t("outbound_communications.col_from"), row.from_address || "—"],
                [t("outbound_communications.col_to"), (row.to_addresses || []).join(", ") || "—"],
                [
                  t("outbound_communications.col_sent_by"),
                  row.sent_by_user?.displayTitle ||
                    row.sent_by_user?.name ||
                    t("outbound_communications.system"),
                ],
                [
                  t("outbound_communications.col_type"),
                  [row.mailer_name, row.mailer_action].filter(Boolean).join("#") || "—",
                ],
                [
                  t("outbound_communications.col_customer"),
                  row.customer?.displayTitle || row.customer?.name || "—",
                ],
              ].map(([label, value]) => (
                <div key={label} className="border-b border-slate-100 px-5 py-3">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {label}
                  </dt>
                  <dd className="mt-1 text-sm text-slate-900">{value}</dd>
                </div>
              ))}
            </dl>
            <div className="border-t border-slate-100 px-5 py-5 text-sm text-slate-800">
              {row.body_html ? (
                <HtmlTextFormatter string={row.body_html} />
              ) : (
                <NewLineFormatter value={row.body_text || ""} />
              )}
            </div>
            <div className="border-t border-slate-100 px-5 py-5">
              <TinyDocumentSection
                title={t("outbound_communications.attachments")}
                module="outbound_communication"
                moduleParam={row.id}
                canUpload={false}
              />
            </div>
          </div>
        )}
      </div>
    </DashboardHeader>
  );
};
